<template>
  <div
    class="group relative mx-auto inline-flex w-fit max-w-full cursor-pointer flex-col overflow-hidden rounded-2xl lg:flex-1 lg:shrink-0"
    @click="onClick"
  >
    <div ref="imageWrapperRef" class="relative w-fit max-w-full">
      <div
        class="w-fit max-w-full overflow-hidden"
        :class="[tallCards ? 'aspect-[376/546]' : 'aspect-[344/230] lg:aspect-[314/214]']"
      >
        <Image
          v-if="image"
          :url="image"
          class="!h-full !w-full"
          :image-class="['h-full w-full object-cover']"
        />
      </div>
      <div class="radio-wrapper absolute right-5 top-5">
        <input
          v-if="!hideRadio"
          type="radio"
          :checked="modelValue === value"
          :value="value"
          class="cursor-pointer !ring-[0px]"
        />
      </div>
    </div>
    <div
      class="z-20 flex min-w-0 max-w-full flex-col justify-between transition-all group-hover:bg-blue-dark lg:flex-1 group-hover:[&_*]:text-white"
      :style="textContainerStyle"
      :class="[
        'w-full',
        modelValue === value || hideRadio ? 'bg-blue-dark [&_*]:text-white' : ' bg-white',
        tallCards ? 'mt-[-90px] px-10 py-9' : 'p-7 sm:px-9 sm:py-11 lg:max-h-[236px]',
      ]"
    >
      <h5
        class="mb-2 min-w-0 break-words font-serif text-[20px] font-bold leading-none text-black [overflow-wrap:anywhere] lg:text-2xl"
        :class="[tallCards ? 'mb-5' : 'lg:mb-5']"
      >
        {{ label }}
      </h5>
      <div class="min-w-0">
        <p
          class="mb-6 line-clamp-3 min-w-0 break-words text-base [overflow-wrap:anywhere]"
          :class="[tallCards ? 'min-h-[40px] leading-tight' : 'min-h-[72px] leading-normal']"
        >
          {{ description }}
        </p>
        <div class="flex w-full flex-wrap justify-start gap-x-[20px] gap-y-2">
          <template v-if="categories && categories.length > 0">
            <span
              v-for="(category, i) in categories"
              :key="category"
              class="relative min-w-0 break-words py-1 font-serif text-lg uppercase leading-normal text-blue [overflow-wrap:anywhere]"
              :class="[
                Number(i) % 2 !== 0 &&
                  'transform before:absolute before:left-[-12px] before:top-1/2 before:h-[5px] before:w-[5px] before:-translate-y-1 before:rounded-full  before:content-[\'\'] group-hover:before:bg-white',
                modelValue === value ? 'before:bg-white' : 'before:bg-blue',
              ]"
            >
              {{ category }}
            </span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import Image from './Image.vue'

const props = withDefaults(
  defineProps<{
    image: string
    label?: string
    description: string
    categories: any
    modelValue: string | number
    value: string | number
    tallCards?: boolean
    hideRadio?: boolean
  }>(),
  {
    label: '',
    description: '',
    tallCards: false,
    hideRadio: false,
  },
)

const emit = defineEmits(['update:modelValue'])

const imageWrapperRef = ref<HTMLElement | null>(null)
const measuredImageWidth = ref<number | null>(null)
let resizeObserver: ResizeObserver | null = null

const syncImageWidth = () => {
  if (!imageWrapperRef.value) return

  const width = imageWrapperRef.value.getBoundingClientRect().width
  measuredImageWidth.value = width > 0 ? width : null
}

onMounted(async () => {
  await nextTick()
  syncImageWidth()

  if (typeof ResizeObserver !== 'undefined' && imageWrapperRef.value) {
    resizeObserver = new ResizeObserver(() => {
      syncImageWidth()
    })

    resizeObserver.observe(imageWrapperRef.value)
    return
  }

  window.addEventListener('resize', syncImageWidth, { passive: true })
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  window.removeEventListener('resize', syncImageWidth)
})

const textContainerStyle = computed(() => {
  if (!measuredImageWidth.value) {
    return { width: '100%', maxWidth: '100%' }
  }

  return {
    width: `${measuredImageWidth.value}px`,
    maxWidth: `${measuredImageWidth.value}px`,
  }
})

const onClick = () => emit('update:modelValue', props.value)
</script>

<style scoped>
.radio-wrapper {
  --borderColor: #ffff;
  --borderWidth: 3px;
}

.radio-wrapper input[type='radio'] {
  -webkit-appearance: none;
  appearance: none;
  vertical-align: middle;
  background: transparent;
  border-radius: 100%;
  display: inline-block;
  border: var(--borderWidth) solid var(--borderColor);
  width: 35px;
  height: 35px;
  position: relative;
}

.radio-wrapper input[type='radio']:checked {
  border: var(--borderWidth) solid var(--borderColor);
  background-color: #ffffff;
}

.radio-wrapper input[type='radio']:after {
  content: '';
  position: absolute;
  background: #0088a4;
  width: 7px;
  height: 3px;
  top: 17px;
  left: 26%;
  transform-origin: left center;
  border-radius: 4px;
}

.radio-wrapper input[type='radio']:checked:after {
  width: calc(var(--borderWidth) * 6);
  transform: rotate(-45deg) translateY(calc(var(--borderWidth) * 2)) scaleX(1);
  transition: transform 200ms ease-out 200ms;
}

.radio-wrapper input[type='radio']:before {
  content: '';
  position: absolute;
  background: #0088a4;
  width: 10px;
  height: 3px;
  top: 15px;
  left: 19%;
  transform-origin: left center;
  border-radius: 4px;
}
.radio-wrapper input[type='radio']:checked:before {
  transform: rotate(45deg) translate(calc(var(--borderWidth) / -2), calc(var(--borderWidth) / -2))
    scaleX(1);
  transition: transform 200ms ease-in;
}

.radio-wrapper input[type='radio']:before {
  transform: rotate(45deg) translate(calc(var(--borderWidth) / -2), calc(var(--borderWidth) / -2))
    scaleX(0);
  transition: transform 200ms ease-in 200ms;
}
.radio-wrapper input[type='radio']:after {
  width: calc(var(--borderWidth) * 4);
  transform: rotate(-45deg) translateY(calc(var(--borderWidth) * 2)) scaleX(0);
  transform-origin: left center;
  transition: transform 200ms ease-in;
}

.radio-wrapper input[type='radio']:focus {
  outline: none !important;
  box-shadow: none !important;
}
</style>
